import { useMemo } from "react";

import { FINANCEIRO_STATUS_OPTIONS, hasFinanceiroObrasFilters } from "@/modules/financeiro-obras";
import type { FinanceiroObraItem } from "@/modules/financeiro-obras";
import { condicaoDoBarco, resolverCondicao } from "@/shared/components";
import type { ObraCardProps, ObrasKanbanColumn } from "@/shared/components";
import { useFrozenWhileEmpty } from "@/shared/hooks";
import {
  FINANCEIRO_CONDICAO_TOOLTIP_PREFIX,
  FINANCEIRO_NO_VALUE,
  FINANCEIRO_SIGLAS,
} from "../../financeiro-obras.constants";
import { useFinanceiroObrasCards } from "../../hooks/use-listing-query";
import type {
  FinanceiroObrasCardsViewModel,
  FinanceiroObrasCardsViewProps,
} from "./types-financeiro-obras-cards-view";

/** Colunas dos Cards: um status por coluna, na ordem do enum. */
const STATUS_COLUMNS = FINANCEIRO_STATUS_OPTIONS.map((option) => ({
  code: option.codigo,
  label: option.descricao,
}));

/** `FinanceiroObraItem` para as props do `ObraCard`: o outlook é o orçado e o RE já vem em percentual. */
function obraCardProps(obra: FinanceiroObraItem): ObraCardProps {
  const condicao = resolverCondicao(obra.condicao);

  return {
    codigo: obra.codigo,
    embarcacao: obra.embarcacao.nome,
    condicao: condicaoDoBarco(obra.condicao),
    condicaoTooltip: condicao
      ? `${FINANCEIRO_CONDICAO_TOOLTIP_PREFIX}: ${condicao.label}`
      : undefined,
    inicio: obra.inicio,
    fim: obra.fim,
    duracaoDias: obra.duracaoDias,
    tipos: obra.tiposObra.map((tipo) => ({ codigo: tipo.codigo, tipo: tipo.nome })),
    realizado: obra.realizadoBrlMilhoes,
    orcado: obra.outlookBrlMilhoes,
    realizadoPercentual: obra.realizadoPercentual,
    barraTooltip: `% ${FINANCEIRO_SIGLAS.re.nome}`,
    metricas: {
      empenhado: obra.empenhadoPercentual,
      comprometido: obra.comprometidoPercentual,
      estimado: obra.estimadoPercentual,
      naoComprometido: obra.naoComprometidoPercentual,
    },
    testId: `financeiro-obra-card-${obra.id}`,
  };
}

export function useFinanceiroObrasCardsView({
  search,
  filters,
  sort,
  isMobile,
}: FinanceiroObrasCardsViewProps): FinanceiroObrasCardsViewModel {
  const columns = useMemo(
    () =>
      STATUS_COLUMNS.filter(
        (option) =>
          filters.statusFinanceiro.length === 0 || filters.statusFinanceiro.includes(option.code),
      ),
    [filters.statusFinanceiro],
  );

  const { columns: state, loadMore } = useFinanceiroObrasCards({
    search,
    filters,
    sort,
    columns: useMemo(() => columns.map((column) => column.code), [columns]),
  });

  const boardColumns = columns.map((column): ObrasKanbanColumn => {
    const data = state[column.code];
    const success = data?.status === "success";

    return {
      code: column.code,
      label: column.label,
      testId: `financeiro-obras-column-${column.code}`,
      isLoading: !data || data.status === "loading",
      errorMessage: data?.status === "error" ? data.errorMessage : null,
      cards: (data?.items ?? []).map((obra) => ({ key: obra.id, props: obraCardProps(obra) })),
      count: success ? data.total : FINANCEIRO_NO_VALUE,
      total: success ? data.total : undefined,
      isLoadingMore: data?.isLoadingMore ?? false,
      loadMore: () => loadMore(column.code),
    };
  });

  const isEmpty =
    columns.length > 0 &&
    columns.every(
      (column) => state[column.code]?.status === "success" && !state[column.code]?.total,
    );

  const empty = useFrozenWhileEmpty(isEmpty, {
    hasSearch: search.trim() !== "",
    hasFilters: hasFinanceiroObrasFilters(filters),
  });

  return {
    device: isMobile ? "mobile" : "desktop",
    boardColumns,
    forbidden: Object.values(state).some((column) => column?.status === "forbidden"),
    isEmpty,
    hasSearch: empty.hasSearch,
    hasFilters: empty.hasFilters,
  };
}
